import React, { useState } from 'react';
import { CreditCard, FileText, ChevronDown, ChevronUp, Calendar, DollarSign, Tag, X, Loader2 } from 'lucide-react';
import StripePaymentForm from '../StripePaymentForm';
import { validateDiscountCode } from '../../../services/discountCodeService';

const ReviewPayment = ({
  bookingData,
  vendor,
  service,
  calculateSubtotal,
  getSubtotalAfterDiscount,
  calculateCommission,
  calculateTax,
  calculateTotal,
  calculateDepositAmount,
  calculatePaymentTerms,
  calculateEndTime,
  calculateProportionalPlatformFee,
  commissionSetting,
  showTaxLine = false,
  activeMarket = null,
  appliedDiscount,
  onApplyDiscount,
  onRemoveDiscount,
  onRetry,
  onPaymentSuccess,
  onPaymentError,
  cardholderName,
  onCardholderNameChange
}) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPaymentTerms, setShowPaymentTerms] = useState(true);
  const [discountInput, setDiscountInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  const hasTerms = vendor?.terms_conditions && vendor.terms_conditions.trim().length > 0;
  const paymentTerms = calculatePaymentTerms?.();
  const subtotalBeforeDiscount = calculateSubtotal?.() ?? 0;
  const subtotal = getSubtotalAfterDiscount ? getSubtotalAfterDiscount() : subtotalBeforeDiscount;
  const totalCommission = calculateCommission();

  const depositPercentage = paymentTerms?.depositPercentage || 50;
  const depositPlatformFee = calculateProportionalPlatformFee
    ? calculateProportionalPlatformFee(depositPercentage)
    : (totalCommission * depositPercentage) / 100;

  return (
    <div>
      <h2 className="marketing-display-md mb-5 text-m-ink">
        Review & payment
      </h2>
      <div className="space-y-4">
        <div className="rounded-2xl border border-m-border-light bg-m-surface p-5">
          <h3 className="mb-4 text-sm font-medium text-m-ink">Booking summary</h3>
          <div className="space-y-1 text-sm">
            <SummaryRow label="Package" value={bookingData.selectedPackage?.name} />
            {(bookingData.selectedAddons || []).map((addon) => (
              <SummaryRow
                key={addon.name}
                label={`Add-on: ${addon.name}`}
                value={`+$${Number(addon.price || 0).toFixed(2)}`}
              />
            ))}
            <SummaryRow label="Date" value={bookingData.eventDate} />
            <SummaryRow label="Time" value={bookingData.eventTime} />
            <SummaryRow label="Duration" value={`${bookingData.duration} hours`} />
            <SummaryRow
              label="Location"
              value={bookingData.location || 'Not specified'}
              alignTop
              last
            />
          </div>

          <div className="mt-6 border-t border-m-border-light pt-6">
            {vendor?.id && (
              <div className="mb-4 border-b border-m-border-light pb-4">
                {!appliedDiscount ? (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="flex flex-1 gap-2">
                      <input
                        type="text"
                        value={discountInput}
                        onChange={(e) => {
                          setDiscountInput(e.target.value.toUpperCase());
                          setDiscountError('');
                        }}
                        placeholder="Discount code"
                        className="auth-input flex-1"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const code = discountInput.trim();
                          if (!code) {
                            setDiscountError('Enter a code');
                            return;
                          }
                          setValidatingDiscount(true);
                          setDiscountError('');
                          try {
                            const res = await validateDiscountCode({
                              vendor_id: vendor.id,
                              code,
                              subtotal_amount: subtotalBeforeDiscount,
                            });
                            if (res.valid && res.data) {
                              onApplyDiscount?.(res.data);
                              setDiscountInput('');
                            } else {
                              setDiscountError(res.message || 'Invalid or expired code');
                            }
                          } catch (e) {
                            setDiscountError(
                              e?.response?.data?.message || 'Could not validate code'
                            );
                          } finally {
                            setValidatingDiscount(false);
                          }
                        }}
                        disabled={validatingDiscount}
                        className="marketing-btn marketing-btn-ink shrink-0 gap-1.5 disabled:opacity-50"
                      >
                        {validatingDiscount ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : null}
                        Apply
                      </button>
                    </div>
                    {discountError && (
                      <p className="text-sm text-red-600">{discountError}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-xl border border-m-border-light bg-m-canvas-light px-3 py-2">
                    <span className="flex items-center gap-2 text-sm font-medium text-m-ink">
                      <Tag className="h-4 w-4" />
                      {appliedDiscount.code}: -$
                      {Number(appliedDiscount.discount_amount).toFixed(2)} applied
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        onRemoveDiscount?.();
                        setDiscountError('');
                      }}
                      className="p-1 text-m-ink-muted hover:text-m-ink"
                      title="Remove code"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-m-ink-muted">Subtotal</span>
                <span className="font-medium text-m-ink">
                  ${subtotal.toFixed(2)}
                  {appliedDiscount && (
                    <span className="ml-1 font-normal text-m-ink-faint">
                      (after ${Number(appliedDiscount.discount_amount).toFixed(2)} discount)
                    </span>
                  )}
                </span>
              </div>
              {commissionSetting &&
                commissionSetting.is_active &&
                calculateCommission() > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-m-ink-muted">Service charge</span>
                    <span className="font-medium text-m-ink">
                      ${calculateCommission().toFixed(2)}
                      {commissionSetting.commission_type === 'percentage' && (
                        <span className="ml-1 text-xs text-m-ink-faint">
                          ({commissionSetting.commission_rate}%)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              {showTaxLine && (
                <div className="flex items-center justify-between">
                  <span className="text-m-ink-muted">Tax</span>
                  <span className="font-medium text-m-ink">
                    ${(calculateTax?.() ?? 0).toFixed(2)}
                  </span>
                </div>
              )}
              {activeMarket?.checkout_legal_copy && (
                <p className="pt-1 text-xs text-m-ink-faint">
                  {activeMarket.checkout_legal_copy}
                </p>
              )}
              <div className="flex items-center justify-between border-t border-m-border-light pt-2">
                <span className="text-m-ink-muted">
                  Deposit {paymentTerms ? `(${paymentTerms.depositLabel})` : '(50%)'}
                </span>
                <span className="font-medium text-m-ink">
                  ${calculateDepositAmount().toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-m-border-light pt-3">
                <span className="font-medium text-m-ink">Amount due now</span>
                <span className="text-lg font-medium text-m-ink">
                  ${calculateDepositAmount().toFixed(2)}
                </span>
              </div>
              <div className="pt-1 text-center text-xs text-m-ink-faint">
                Full total: ${calculateTotal().toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {paymentTerms && (
          <div className="rounded-2xl border border-m-border-light bg-m-canvas-light p-5 sm:p-6">
            <button
              type="button"
              onClick={() => setShowPaymentTerms(!showPaymentTerms)}
              className="mb-4 flex w-full items-center justify-between text-left"
            >
              <div className="flex items-center">
                <DollarSign className="mr-3 h-5 w-5 text-m-ink" />
                <h3 className="text-base font-medium text-m-ink">Payment terms</h3>
              </div>
              {showPaymentTerms ? (
                <ChevronUp className="h-5 w-5 text-m-ink-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-m-ink-muted" />
              )}
            </button>
            {showPaymentTerms && (
              <div className="space-y-4">
                <div className="rounded-xl border border-m-border-light bg-m-surface p-4">
                  {paymentTerms.matchedWindowLabel && (
                    <p className="mb-2 text-xs font-medium text-m-ink">
                      Applies: {paymentTerms.matchedWindowLabel}
                      {paymentTerms.daysUntilEvent != null &&
                        ` (${paymentTerms.daysUntilEvent} days until event)`}
                    </p>
                  )}
                  <p className="mb-4 text-sm text-m-ink-muted">
                    {paymentTerms.description}
                  </p>

                  <div className="space-y-3">
                    <h4 className="mb-2 text-sm font-medium text-m-ink">
                      Payment schedule
                    </h4>
                    {paymentTerms.schedule.map((payment, index) => {
                      const proportionalFee =
                        payment.platformFeePortion ??
                        (calculateProportionalPlatformFee
                          ? calculateProportionalPlatformFee(payment.percentage)
                          : (totalCommission * payment.percentage) / 100);
                      const paymentAmount =
                        payment.totalAmount ??
                        (subtotal * payment.percentage) / 100 + proportionalFee;

                      return (
                        <div
                          key={index}
                          className="flex items-start justify-between border-b border-m-border-light py-2 last:border-0"
                        >
                          <div className="flex-1">
                            <div className="mb-1 flex items-center">
                              <Calendar className="mr-2 h-4 w-4 text-m-ink" />
                              <span className="text-sm font-medium text-m-ink">
                                {payment.label}
                              </span>
                              <span className="ml-2 text-xs text-m-ink-faint">
                                ({payment.percentage}%)
                              </span>
                            </div>
                            <p className="ml-6 text-xs text-m-ink-muted">
                              {payment.dueDate}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-m-ink">
                              ${paymentAmount.toFixed(2)}
                            </span>
                            {proportionalFee > 0 && (
                              <p className="mt-1 text-xs text-m-ink-faint">
                                (includes ${proportionalFee.toFixed(2)} platform fee)
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border border-m-border-light bg-m-surface p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-m-ink">Amount due now</span>
                    <span className="text-lg font-medium text-m-ink">
                      ${calculateDepositAmount().toFixed(2)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-m-ink-muted">
                    Booking deposit ({paymentTerms.depositLabel} of package)
                    {paymentTerms.amountDueNow >
                    (paymentTerms.bookingDepositAmount ?? 0) + 0.01
                      ? ' plus any milestones already due by your event date'
                      : ''}{' '}
                    + platform fee on checkout amount
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {hasTerms && (
          <div className="rounded-2xl border border-m-border-light bg-m-surface p-5">
            <button
              type="button"
              onClick={() => setShowTerms(!showTerms)}
              className="flex w-full items-center justify-between text-left"
            >
              <div className="flex items-center">
                <FileText className="mr-2 h-4 w-4 text-m-ink" />
                <h3 className="text-sm font-medium text-m-ink">Terms & conditions</h3>
              </div>
              {showTerms ? (
                <ChevronUp className="h-4 w-4 text-m-ink-muted" />
              ) : (
                <ChevronDown className="h-4 w-4 text-m-ink-muted" />
              )}
            </button>
            {showTerms && (
              <div className="mt-3 border-t border-m-border-light pt-3">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-m-ink-muted">
                  {vendor.terms_conditions}
                </p>
                <p className="mt-3 text-xs italic text-m-ink-faint">
                  By proceeding with payment, you agree to the above terms and conditions.
                </p>
              </div>
            )}
          </div>
        )}

        <div>
          <h3 className="mb-3 text-sm font-medium text-m-ink">Payment information</h3>
          {bookingData.selectedPackage && vendor && service ? (
            <StripePaymentForm
              amount={calculateDepositAmount()}
              bookingData={{
                vendor_id: vendor.id,
                service_id: service.id,
                package_id: bookingData.selectedPackage.id,
                addons: (bookingData.selectedAddons || []).map((addon) => ({
                  name: addon.name,
                  description: addon.description || '',
                  price: Number(addon.price || 0),
                })),
                event_date: bookingData.eventDate,
                start_time: bookingData.eventTime,
                end_time: calculateEndTime(bookingData.eventTime, bookingData.duration),
                duration_hours: parseFloat(bookingData.duration),
                guest_count: parseInt(bookingData.guestCount) || null,
                event_type: bookingData.eventType,
                event_location: bookingData.location,
                event_address: bookingData.location,
                event_country: bookingData.eventCountry || null,
                billing_country: bookingData.billingCountry || null,
                customer_type: bookingData.customerType || 'individual',
                customer_tax_id: bookingData.customerTaxId || null,
                special_requirements: bookingData.specialRequests,
                subtotal_amount: subtotal,
                platform_fee: totalCommission,
                tax_amount: calculateTax?.() ?? 0,
                payment_platform_fee: depositPlatformFee,
                payment_percentage: depositPercentage,
                commission_rate: commissionSetting?.commission_rate || null,
                commission_type: commissionSetting?.commission_type || null,
                commission_setting_id: commissionSetting?.id || null,
                discount_code_id: appliedDiscount?.discount_code_id || null,
                discount_amount: appliedDiscount?.discount_amount ?? 0,
                total_amount: calculateTotal(),
                deposit_amount: calculateDepositAmount(),
              }}
              paymentType={depositPercentage === 100 ? 'full_payment' : 'deposit'}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              metadata={{
                cardholderName: cardholderName,
                onCardholderNameChange: onCardholderNameChange
              }}
            />
          ) : (
            <div className="rounded-2xl border border-m-border-light bg-m-surface p-5">
              <div className="flex items-center justify-center py-6">
                <div className="text-center">
                  <div className="mx-auto mb-3 flex h-10 w-10 animate-pulse items-center justify-center rounded-full bg-m-canvas-light">
                    <CreditCard className="h-4 w-4 text-m-ink-faint" />
                  </div>
                  <p className="mb-3 text-sm text-m-ink-muted">Missing booking information</p>
                  <button
                    type="button"
                    onClick={onRetry}
                    className="marketing-btn marketing-btn-ink"
                  >
                    Go back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function SummaryRow({ label, value, alignTop = false, last = false }) {
  return (
    <div
      className={`flex ${alignTop ? 'items-start' : 'items-center'} justify-between py-2 ${
        last ? '' : 'border-b border-m-border-light'
      }`}
    >
      <span className="text-m-ink-muted">{label}</span>
      <span
        className={`font-medium text-m-ink ${alignTop ? 'max-w-[60%] text-right' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

export default ReviewPayment;
